import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { Announcement } from "../../data/announcements"
import apiClient from "../../api/apiClient"
import { serializeFirestoreData } from "../../utils/timestamp"

interface AnnouncementsState {
  announcements: Announcement[]
  isLoading: boolean
  error: string | null
}

const initialState: AnnouncementsState = {
  announcements: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchAnnouncements = createAsyncThunk(
  "announcements/fetchAnnouncements",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/announcements")
      return serializeFirestoreData(response.data.announcements)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch announcements")
    }
  },
)

export const getAnnouncementById = createAsyncThunk(
  "announcements/getAnnouncementById",
  async (announcementId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/announcements/${announcementId}`)
      return serializeFirestoreData(response.data.announcement)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to get announcement")
    }
  },
)

// Update the createAnnouncement interface
export const createAnnouncement = createAsyncThunk(
  "announcements/createAnnouncement",
  async (
    announcementData: {
      title: string
      content: string
      buildings: string[]
      sendEmail?: boolean
      enablePushNotification?: boolean
      showExpanded?: boolean
      image?: File
    },
    { rejectWithValue },
  ) => {
    try {
      const { image, ...data } = announcementData
      const formData = new FormData()
      formData.append("title", data.title)
      formData.append("content", data.content)
      formData.append("buildings", JSON.stringify(data.buildings))
      formData.append("sendEmail", String(data.sendEmail || false))
      formData.append("enablePushNotification", String(data.enablePushNotification || false))
      formData.append("showExpanded", String(data.showExpanded || false))
      if (image) {
        formData.append("image", image)
      }

      const response = await apiClient.post("/announcements", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      return { success: true, announcement: serializeFirestoreData(response.data.announcement) }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to create announcement")
    }
  },
)

// Update the updateAnnouncement interface
export const updateAnnouncement = createAsyncThunk(
  "announcements/updateAnnouncement",
  async (
    announcementData: {
      id: string
      title: string
      content: string
      buildings: string[]
      sendEmail?: boolean
      enablePushNotification?: boolean
      showExpanded?: boolean
      image?: File
      removeImage?: boolean
    },
    { rejectWithValue },
  ) => {
    try {
      const { id, image, ...data } = announcementData
      const formData = new FormData()
      formData.append("title", data.title)
      formData.append("content", data.content)
      formData.append("buildings", JSON.stringify(data.buildings))
      formData.append("sendEmail", String(data.sendEmail || false))
      formData.append("enablePushNotification", String(data.enablePushNotification || false))
      formData.append("showExpanded", String(data.showExpanded || false))
      formData.append("removeImage", String(data.removeImage || false))
      if (image) {
        formData.append("image", image)
      }

      const response = await apiClient.put(`/announcements/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      return { success: true, announcement: serializeFirestoreData(response.data.announcement) }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to update announcement")
    }
  },
)

export const deleteAnnouncement = createAsyncThunk(
  "announcements/deleteAnnouncement",
  async (announcementId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/announcements/${announcementId}`)
      return { success: true, announcementId }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to delete announcement")
    }
  },
)

const announcementsSlice = createSlice({
  name: "announcements",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch announcements
      .addCase(fetchAnnouncements.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action: PayloadAction<Announcement[]>) => {
        state.announcements = action.payload
        state.isLoading = false
      })
      .addCase(fetchAnnouncements.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Get announcement by ID
      .addCase(getAnnouncementById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getAnnouncementById.fulfilled, (state, action) => {
        const index = state.announcements.findIndex((a) => a.id === action.payload.id)
        if (index !== -1) {
          state.announcements[index] = action.payload
        } else {
          state.announcements.push(action.payload)
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(getAnnouncementById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Create announcement
      .addCase(createAnnouncement.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createAnnouncement.fulfilled, (state, action) => {
        if (action.payload.announcement) {
          state.announcements.unshift(action.payload.announcement as Announcement)
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update announcement
      .addCase(updateAnnouncement.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        if (action.payload.announcement) {
          const index = state.announcements.findIndex((a) => a.id === action.payload.announcement.id)
          if (index !== -1) {
            state.announcements[index] = {
              ...state.announcements[index],
              ...action.payload.announcement,
            }
          }
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Delete announcement
      .addCase(deleteAnnouncement.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.announcements = state.announcements.filter((a) => a.id !== action.payload.announcementId)
        state.isLoading = false
        state.error = null
      })
      .addCase(deleteAnnouncement.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export default announcementsSlice.reducer
